/**
 * EOM 实验对象模型 —— 所有器材、试剂、连接、过程进入统一对象模型。
 * 对应技术设计 §4.3。
 */

import type { Port, Slot } from "./port.js";

export interface Pose {
  x: number;
  y: number;
  rotation: number;
  scale?: number;
  zIndex?: number;
}

/**
 * 器材能力。**22 项，与技术设计 §4.3（`:285-307`）完全一致，不得增删改名**。
 * 末三项由 v1.3 依据需求文档 001 补入：
 *   ignitable   酒精灯/火柴/燃烧匙/蜡烛 —— ignite 关系
 *   switchable  开关/电源             —— power 关系
 *   pressurizable 密闭容器            —— 气密性检查、液面上升、气球膨胀、倒吸判定
 */
export type Capability =
  | "movable"
  | "rotatable"
  | "heatable"
  | "container"
  | "connector"
  | "clampable"
  | "pourable"
  | "collectable"
  | "weighable"
  | "measurable"
  | "stirrable"
  | "filterable"
  | "evaporable"
  | "electrolyzable"
  | "flammable"
  | "ph_testable"
  | "indicator_reactive"
  | "precipitable"
  | "comparable"
  | "ignitable"
  | "switchable"
  | "pressurizable";

/** 契约自检用：运行期可枚举的能力全集，顺序与上面的联合类型一致。 */
export const CAPABILITIES: readonly Capability[] = [
  "movable",
  "rotatable",
  "heatable",
  "container",
  "connector",
  "clampable",
  "pourable",
  "collectable",
  "weighable",
  "measurable",
  "stirrable",
  "filterable",
  "evaporable",
  "electrolyzable",
  "flammable",
  "ph_testable",
  "indicator_reactive",
  "precipitable",
  "comparable",
  "ignitable",
  "switchable",
  "pressurizable",
] as const;

/** 器材分类（§4.3 器材分类树） */
export type EquipmentCategory =
  | "Container"
  | "Meter"
  | "Tool"
  | "Connector"
  | "Reagent"
  | "Power"
  | "Consumable"
  | "Environment";

export const EQUIPMENT_CATEGORIES: readonly EquipmentCategory[] = [
  "Container",
  "Meter",
  "Tool",
  "Connector",
  "Reagent",
  "Power",
  "Consumable",
  "Environment",
] as const;

export interface LabObject {
  id: string;
  type: string;
  name: string;
  pose: Pose;
  state: Record<string, unknown>;
  ports?: Port[];
  slots?: Slot[];
  capabilities: Capability[];
}

export function hasCapability(object: LabObject, capability: Capability): boolean {
  return object.capabilities.includes(capability);
}

export function findPort(object: LabObject, portId: string): Port | undefined {
  return object.ports?.find((port) => port.id === portId);
}

export function findSlot(object: LabObject, slotId: string): Slot | undefined {
  return object.slots?.find((slot) => slot.id === slotId);
}

/**
 * 器材局部坐标 → 世界坐标。含 scale 与 rotation，与 svg-layer 的
 * `translate(x y) rotate(r) scale(s)` 变换顺序严格一致 —— 两边不一致会让
 * 「看着连上了但锚点对不上」，正是 §22 红线 2 要防的穿帮。
 */
export function toWorld(object: LabObject, local: { x: number; y: number }): { x: number; y: number } {
  const scale = object.pose.scale ?? 1;
  const radians = (object.pose.rotation * Math.PI) / 180;
  const localX = local.x * scale;
  const localY = local.y * scale;

  return {
    x: object.pose.x + localX * Math.cos(radians) - localY * Math.sin(radians),
    y: object.pose.y + localX * Math.sin(radians) + localY * Math.cos(radians),
  };
}

/** 世界坐标 → 器材局部坐标。`toWorld` 的逆变换，拖拽落点换算要用。 */
export function toLocal(object: LabObject, world: { x: number; y: number }): { x: number; y: number } {
  const scale = object.pose.scale ?? 1;
  const radians = (object.pose.rotation * Math.PI) / 180;
  const dx = world.x - object.pose.x;
  const dy = world.y - object.pose.y;

  return {
    x: (dx * Math.cos(radians) + dy * Math.sin(radians)) / scale,
    y: (-dx * Math.sin(radians) + dy * Math.cos(radians)) / scale,
  };
}

/** 端口局部锚点 → 世界坐标（ConstraintSolver 直接用这个，§5.5）。 */
export function portWorldPosition(object: LabObject, portId: string): { x: number; y: number } | null {
  const port = findPort(object, portId);
  if (!port) return null;
  return toWorld(object, port.anchor);
}

/** 槽位锚点 → 世界坐标。 */
export function slotWorldPosition(object: LabObject, slotId: string): { x: number; y: number } | null {
  const slot = findSlot(object, slotId);
  if (!slot) return null;
  return toWorld(object, slot.anchor);
}
